import React from 'react';

const BYOEContent = () => {
  return (
    <div>
      <h2>Use Your Own AI Models (BYOE)</h2>
      <p>
        <strong>Bring Your Own Endpoints (BYOE)</strong> lets you connect your
        own AI model API keys to Stackform so every agent — across the
        Orchestrator, Generalist, Researcher, Evaluator, and Challenger stacks —
        runs on models you control and trust.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '0.75rem',
        margin: '1rem 0 1.5rem',
      }}>
        {[
          { icon: '🔒', title: 'Privacy', body: 'Your API key means your requests go directly to your provider — not through a shared pool.' },
          { icon: '💰', title: 'Cost control', body: 'Use free-tier or lower-cost models where they\'re sufficient; reserve premium models for the agents that need them.' },
          { icon: '🎛️', title: 'Model choice', body: 'Pick the exact model version and provider that matches your accuracy, speed, and compliance requirements.' },
        ].map(({ icon, title, body }) => (
          <div key={title} style={{
            padding: '0.75rem',
            border: '1px solid var(--border-color, #ddd)',
            borderRadius: '8px',
            fontSize: '0.88rem',
          }}>
            <div style={{ fontSize: '1.4rem', marginBottom: '0.25rem' }}>{icon}</div>
            <strong>{title}</strong>
            <p style={{ margin: '0.25rem 0 0', color: 'var(--text-secondary, #666)' }}>{body}</p>
          </div>
        ))}
      </div>

      <p>
        BYOE is completely optional. If you don't configure it, Stackform uses
        its default models automatically.
      </p>

      <div style={{
        padding: '0.75rem 1rem',
        backgroundColor: 'var(--bg-secondary, #fff3cd)',
        border: '1px solid var(--warning-color, #ffc107)',
        borderRadius: '8px',
        marginBottom: '1.5rem',
        fontSize: '0.9rem',
      }}>
        <strong>💡 Mix and match providers per agent.</strong> You don't have
        to use the same model everywhere. For example: use a fast, cost-efficient
        model for the Orchestrator, and a more capable research-grade model for
        the Researcher Agent. Stackform routes each agent to its assigned model
        independently.
      </div>

      <h3>Getting an API key</h3>
      <p>You'll need an API key from your chosen provider. Here's where to get one:</p>

      <ul>
        <li>
          <a href="https://aistudio.google.com/welcome" target="_blank" rel="noopener noreferrer">
            Gemini (Google AI Studio)
          </a>
          <ul>
            <li>Click <strong>"Get an API key"</strong></li>
            <li>Sign in, then select <strong>API Keys</strong> in the left nav</li>
            <li>Click <strong>"Create API key"</strong> and follow the prompts</li>
          </ul>
        </li>
        <li>
          <a href="https://platform.claude.com/login" target="_blank" rel="noopener noreferrer">
            Claude (Anthropic)
          </a>
          <ul>
            <li>Sign in and set up your account</li>
            <li>Click <strong>"Get API Key"</strong> and follow the prompts</li>
          </ul>
        </li>
        <li>
          <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">
            OpenAI
          </a>
          <ul>
            <li>Sign in, select <strong>API Keys</strong> in the left nav</li>
            <li>Click <strong>"Create new secret key"</strong> and follow the prompts</li>
          </ul>
        </li>
      </ul>

      <p>For enterprise cloud providers:</p>
      <ul>
        <li>
          <a href="https://learn.microsoft.com/en-us/azure/ai-foundry/what-is-foundry?view=foundry" target="_blank" rel="noopener noreferrer">
            Azure AI Foundry (Microsoft)
          </a>
        </li>
        <li>
          <a href="https://docs.aws.amazon.com/bedrock/latest/userguide/getting-started.html" target="_blank" rel="noopener noreferrer">
            Amazon Bedrock (AWS)
          </a>
        </li>
        <li>
          <a href="https://cloud.google.com/vertex-ai/generative-ai/docs/overview" target="_blank" rel="noopener noreferrer">
            Google Cloud Vertex AI
          </a>
        </li>
      </ul>

      <h3>Connecting your key in Stackform</h3>
      <ol>
        <li>Open <strong>User Settings → API Keys</strong>.</li>
        <li>Select your provider and paste in your API key (and endpoint URL if required).</li>
        <li>Go to <strong>User Settings → Model Defaults → Agent Models</strong> and assign a provider and model to each subagent (Aeri, Capo, Cardian), or set an agent's provider to <strong>None</strong> to disable it. Enabled agents run inside every stack.</li>
        <li>Save. Your agents will now use your keys for all subsequent requests.</li>
      </ol>

      <p style={{ marginTop: '1rem' }}>
        <strong>Multiple keys per provider.</strong> You can store more than one
        configuration for the same provider (for example, two separate Azure
        keys) and point different agents at different keys.
      </p>

      <h3>Run agents on your own device (local models)</h3>
      <p>
        Prefer to keep everything on your own hardware? Stackform can route any
        agent to a <strong>local model</strong> running on your machine via{' '}
        <strong>Stackform Connect</strong> — your data never leaves your device,
        at zero per-token cost.
      </p>
      <ol>
        <li>
          Install a local model server such as{' '}
          <a href="https://ollama.com/" target="_blank" rel="noopener noreferrer">Ollama</a>{' '}
          and pull the models you want to use.
        </li>
        <li>
          Open <strong>User Settings → Connect</strong>, create a device token,
          and connect your device.
        </li>
        <li>
          In <strong>User Settings → Model Defaults</strong>, pick your connected
          device for any agent — its installed models appear in the dropdown.
          (If the device is offline, Stackform shows it as unavailable.)
        </li>
      </ol>

      <h3>Switch setups with Model Profiles</h3>
      <p>
        Save the cloud setup above as one profile and an all-local setup as
        another (for example a <strong>Private</strong> profile), then switch
        the active profile in a click from the model selector in the chat
        panel — handy for routing sensitive work to local models while keeping a
        faster cloud profile for everything else. Each profile keeps its own
        main model, summarizer, and per-agent assignments; create, duplicate,
        rename, and delete them under{' '}
        <strong>User Settings → Model Defaults</strong>.
      </p>

      <h3>Pick a dedicated summarizer model (optional)</h3>
      <p>
        Under <strong>User Settings → Model Defaults</strong> you can set a
        separate <strong>Summarizer Model</strong> — the model used to
        synthesize multi-agent reports in Workflows. Leave it empty to reuse
        your main model.
      </p>
    </div>
  );
};

export default BYOEContent;
